import { Empty } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import dayjs from "dayjs";
import { FC, useCallback, useMemo } from "react";
import { CheckoutOrder, PaymentForm } from "tm-shared/types";
import { formatDate } from "tm-shared/utils";

import FlexContainer from "@/components/FlexContainer";
import { useAppSelector } from "@/hooks/redux";

import CheckoutAccordion, { ICheckoutAccordion } from "../CheckoutAccordion";
import { StatisticAccordion } from "../StatisticAccordion";
import { StyledCLCollapse } from "./Styled";

interface ICheckoutList {
    chosenOrders: Record<string, boolean>;
    setChosenOrders: React.Dispatch<
        React.SetStateAction<Record<string, boolean>>
    >;
    resetCheckouts: () => void;
}

const calculatePaymentDate = (
    isPayOnDelivery: boolean,
    effectiveDate: string,
    paymentForm: PaymentForm,
    dayInterval: number | undefined,
): string => {
    if (isPayOnDelivery || !effectiveDate) {
        return effectiveDate;
    }

    const interval =
        paymentForm === PaymentForm.Cash1 ? dayInterval : dayInterval;

    return dayjs(effectiveDate, "YYYY-MM-DD")
        .add(interval || 0, "day")
        .format("YYYY-MM-DD");
};

type CheckoutEntry = [string, CheckoutOrder];

export const CheckoutList: FC<ICheckoutList> = ({
    chosenOrders,
    setChosenOrders,
    resetCheckouts,
}) => {
    const {
        checkoutOpenedList,
        checkoutClosedList,
        ignoreRemainingsOrders,
        markets,
    } = useAppSelector((state) => ({
        checkoutOpenedList: state.work.checkoutList,
        checkoutClosedList: state.work.checkoutCompletedList,
        ignoreRemainingsOrders: state.offline.ignoreRemainingsOrders,
        markets: state.work.markets,
    }));

    const { checkoutDetails } = useAppSelector((s) => s.progress);

    const checkoutMainList = useMemo(() => {
        const sortByDateDesc = <T extends Record<string, CheckoutOrder>>(
            list: T,
        ): T => {
            const entries = Object.entries(list) as CheckoutEntry[];

            const sorted = entries.sort(([, a], [, b]) => {
                const dateA = dayjs(a.creationDate);
                const dateB = dayjs(b.creationDate);
                return dateB.diff(dateA);
            });

            return Object.fromEntries(sorted) as T;
        };

        return {
            ...sortByDateDesc(checkoutOpenedList),
            ...sortByDateDesc(checkoutClosedList),
        };
    }, [checkoutOpenedList, checkoutClosedList]);

    const handleCheckboxChange = useCallback(
        (e: CheckboxChangeEvent, orderId: string) => {
            setChosenOrders((prev) => ({
                ...prev,
                [orderId]: e.target.checked,
            }));
        },
        [setChosenOrders],
    );

    const hasCheckouts = useMemo(
        () => Object.keys(checkoutMainList).length > 0,
        [checkoutMainList],
    );

    const checkoutValues = useMemo(
        () => Object.values(checkoutMainList),
        [checkoutMainList],
    );

    return (
        <>
            {!hasCheckouts ? (
                <FlexContainer
                    justifyContent="center"
                    alignItems="center"
                    gap="8px"
                    width="100%"
                >
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Немає замовлень"
                    />
                </FlexContainer>
            ) : (
                <StatisticAccordion checkouts={checkoutValues} />
            )}

            <StyledCLCollapse accordion>
                {Object.entries(checkoutMainList).map(
                    ([orderId, checkout], index) => {
                        const {
                            marketName,
                            name,
                            paymentForm,
                            id: invoiceId,
                            address,
                            marketId,
                            closed,
                            contragent,
                            isCarrier,
                            comments,
                            orderType,
                            isSelfDelivery,
                            creationDate,
                            shipmentDate,
                            products,
                            isCredit,
                            customShipmentDate,
                        } = checkout;

                        const currentDetails = checkoutDetails[orderId];

                        const currentMarket = markets[marketId];
                        const isPayOnDelivery =
                            !!currentDetails?.isPayOnDelivery;
                        const hasCustomDate =
                            customShipmentDate && customShipmentDate !== "-";
                        const effectiveDate = hasCustomDate
                            ? customShipmentDate
                            : shipmentDate;

                        const paymentInterval =
                            paymentForm === PaymentForm.Cash1
                                ? currentMarket?.context?.paymentF1DayInterval
                                : currentMarket?.context?.paymentF2DayInterval;

                        const paymentDateValue = calculatePaymentDate(
                            isPayOnDelivery,
                            effectiveDate,
                            paymentForm,
                            paymentInterval,
                        );

                        const baseProps: ICheckoutAccordion = {
                            index,
                            closed,
                            invoiceId: invoiceId || `TM-${orderId}`,
                            isOfflineOrder: !invoiceId,
                            orderId,
                            marketId,
                            products,
                            ignoreRemainings:
                                !!ignoreRemainingsOrders?.[orderId],
                            orderType,
                            address,
                            contragent,
                            companyName: marketName || name,
                            paymentForm,
                            isChosen: !!chosenOrders[orderId],
                            paymentDate: formatDate(paymentDateValue),
                            shipmentDate: formatDate(shipmentDate),
                            orderDate: creationDate,
                            handleChangeCheckoutChoice: handleCheckboxChange,
                            handleResetCheckouts: resetCheckouts,
                            isCarrier: false,
                            isSelfDelivery: false,
                            isPayOnDelivery: false,
                            payOnDeliveryComment: "",
                            customShipmentDate: formatDate(customShipmentDate),
                        };

                        const dynamicProps = closed
                            ? {
                                  isCarrier,
                                  isSelfDelivery,
                                  isPayOnDelivery: !isCredit,
                                  payOnDeliveryComment: comments.length
                                      ? comments.map((c) => `• ${c}`).join("\n")
                                      : "",
                              }
                            : {
                                  isCarrier: !!currentDetails?.isCarrier,
                                  isSelfDelivery:
                                      !!currentDetails?.isSelfPickup,
                                  isPayOnDelivery,
                                  payOnDeliveryComment:
                                      currentDetails?.comment ?? "",
                              };

                        return (
                            <CheckoutAccordion
                                key={orderId}
                                {...baseProps}
                                {...dynamicProps}
                            />
                        );
                    },
                )}
            </StyledCLCollapse>
        </>
    );
};
